import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { fetchStatus } from "../../lib/api";

export function LoginPage(): React.ReactNode {
  const [tenantId, setTenantId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolvingTenant, setResolvingTenant] = useState(false);
  const { tenant = "demo" } = useParams<{ tenant: string }>();

  const { login, error: authError, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      setResolvingTenant(true);
      try {
        const status = await fetchStatus(tenant);
        if (!aborted) {
          setTenantId(status.tenant.id);
          setError(null);
        }
      } catch {
        if (!aborted) {
          setTenantId("");
          setError("Tenant nicht gefunden");
        }
      } finally {
        if (!aborted) {
          setResolvingTenant(false);
        }
      }
    };

    run();
    return () => {
      aborted = true;
    };
  }, [tenant]);

  useEffect(() => {
    if (user) {
      navigate(`/${tenant}/admin`, { replace: true });
    }
  }, [user, tenant, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenantId) {
      setError("Tenant-ID konnte nicht ermittelt werden");
      return;
    }
    setLoading(true);
    const ok = await login({ tenantId, email, password });
    setLoading(false);
    if (ok) {
      navigate(`/${tenant}/admin`, { replace: true });
      return;
    }
    setError(authError ?? "Login fehlgeschlagen");
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md items-center justify-center px-4 dark:bg-slate-950 dark:text-slate-100">
      <form
        className="w-full space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow dark:border-slate-700 dark:bg-slate-900"
        onSubmit={handleSubmit}
        aria-label="Admin-Login"
      >
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Admin Login</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">Tenant: {tenant}</p>

        <label className="block space-y-1 text-sm text-slate-700 dark:text-slate-300" htmlFor="login-email">
          E-Mail
          <input
            id="login-email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </label>

        <label className="block space-y-1 text-sm text-slate-700 dark:text-slate-300" htmlFor="login-password">
          Passwort
          <input
            id="login-password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </label>

        {(error ?? authError) && <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200">{error ?? authError}</p>}

        <button
          type="submit"
          disabled={loading || resolvingTenant}
          className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-400"
        >
          {loading ? "Anmelden..." : "Anmelden"}
        </button>
      </form>
    </div>
  );
}
